import { useEffect, useMemo, useState } from "react";
import { useUpdateOrderStatusMutation } from "../../../../../../../../API/authApi";
import { ICartItem, IDeliveryOrderData, IOrderParams, IPreOrderData, IReserveOrderData } from "../../../../../../../../Interfaces/componentTypes";
import { OrderStatus } from "../../../../../../../../Interfaces/formValue-interfaces";
import useCompanyOrderCards from "../Card/companyOrderCard-hook";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import { IOrderResponse } from "../../../../../../../../Interfaces/responseTypes";
import { useToastify } from "../../../../../../../../Hooks/hooks";

const useCompanyOrderItem = (order: IOrderResponse, currencySymbol: string) => {
    const { t, activeLanguage } = useLanguagesHooks();
    const { showToastSuccess, showToastError } = useToastify();
    const [localStatus, setLocalStatus] = useState<OrderStatus>(order.status);
    const { getOrderCardContent } = useCompanyOrderCards(currencySymbol);
    const [updateOrderStatus] = useUpdateOrderStatusMutation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const orderParams: IOrderParams = JSON.parse(order.order_parameters);
    const orderData: IDeliveryOrderData | IPreOrderData | IReserveOrderData = JSON.parse(order.data);

    const { typeLabel } = getOrderCardContent(
        order.order_type,
        orderParams,
        order.data
    );

    const onAcceptOrder = async () => {
        try {
            await updateOrderStatus({
                order_id: order.id,
                body: {
                    company_id: orderParams.company_id,
                    status: OrderStatus.ACCEPTED
                }
            }).unwrap();
            showToastSuccess(t('Toast.Success.Common'));
            setLocalStatus(OrderStatus.ACCEPTED);
        } catch (err) {
            showToastError(t('Toast.Error.Common'))
        }
    };

    const onRejectOrder = async () => {
        try {
            await updateOrderStatus({
                order_id: order.id,
                body: {
                    company_id: orderParams.company_id,
                    status: OrderStatus.REJECTED
                }
            }).unwrap();
            showToastSuccess(t('Toast.Success.Common'));
            setLocalStatus(OrderStatus.REJECTED);
        } catch (err) {
            showToastError(t('Toast.Error.Common'))
        }
    };

    const groupedCart = useMemo(() => {
        const grouped: Record<string, ICartItem[]> = {};
        orderData.cart.forEach((item) => {
            const categoryName =
                (item.category[
                    `name_${activeLanguage}` as keyof typeof item.category
                ] as string) || item.category.name_en;

            if (!grouped[categoryName]) {
                grouped[categoryName] = [];
            }
            grouped[categoryName].push(item);
        });
        return grouped;
    }, [orderData.cart, activeLanguage]);
    const getLocalizedTitle = (item: ICartItem): string =>
        (item[`name_${activeLanguage}` as keyof ICartItem] as string) ||
        item.name_en;

    return {
        typeLabel,
        localStatus,
        orderParams,
        orderData,
        groupedCart,
        onAcceptOrder,
        onRejectOrder,
        setLocalStatus,
        getLocalizedTitle
    }

};

export default useCompanyOrderItem